import DateInput from '@/components/DateInput'
import { HoverHelp } from '@/tableControls'
import type { QboTransactionSearchCriteria } from '@/services/qboTransactionSearch'

const CONFLICT_SIMILARITY_OPTIONS: { value: number; label: string }[] = [
  { value: 100, label: 'Exact (100)' },
  { value: 95, label: '95' },
  { value: 90, label: '90' },
  { value: 80, label: '80' },
  { value: 70, label: '70' },
]

function getFilterInputClass(value: string): string {
  const baseClass = 'input-sm'
  return value ? `${baseClass} ring-2 ring-blue-400 bg-blue-50` : baseClass
}

export interface TxnConflictSearchBarProps {
  criteria: QboTransactionSearchCriteria
  onChange: <K extends keyof QboTransactionSearchCriteria>(
    key: K,
    value: QboTransactionSearchCriteria[K]
  ) => void
  idPrefix?: string
}

/** Compact conflict-mode criteria: start date, end date, similarity — one row. */
export default function TxnConflictSearchBar({
  criteria,
  onChange,
  idPrefix = 'txn-conflict',
}: TxnConflictSearchBarProps) {
  return (
    <div className="filter-bar filter-bar--txn-conflict">
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-start-date`}
          className={`text-xs font-medium ${
            criteria.date_start ? 'text-blue-600' : 'text-gray-800'
          }`}
        >
          Start date *
        </label>
        <DateInput
          className={getFilterInputClass(criteria.date_start ?? '')}
          value={criteria.date_start ?? ''}
          onChange={(v) => onChange('date_start', v)}
          dateFormat="iso"
          placeholder="yyyy-mm-dd"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-end-date`}
          className={`text-xs font-medium ${
            criteria.date_end ? 'text-blue-600' : 'text-gray-800'
          }`}
        >
          End date *
        </label>
        <DateInput
          className={getFilterInputClass(criteria.date_end ?? '')}
          value={criteria.date_end ?? ''}
          onChange={(v) => onChange('date_end', v)}
          dateFormat="iso"
          placeholder="yyyy-mm-dd"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-similarity`}
          className={`min-w-0 text-xs font-medium ${
            (criteria.conflict_similarity ?? 90) !== 90 ? 'text-blue-600' : 'text-gray-600'
          }`}
        >
          <HoverHelp label="Description similarity" aria-label="About description similarity">
            <p>
              How closely line-item descriptions must match within the same vendor to form a group.
              Exact (100) requires the same normalized description; lower values also group near
              matches (for example, descriptions that differ only by month or invoice noise).
            </p>
          </HoverHelp>
        </label>
        <select
          id={`${idPrefix}-similarity`}
          className="select input-sm w-full"
          value={criteria.conflict_similarity ?? 90}
          onChange={(e) => onChange('conflict_similarity', Number(e.target.value))}
        >
          {CONFLICT_SIMILARITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
